import React, { useEffect } from "react";
import { Box, Button, Step, StepLabel, Stepper, ThemeProvider, Typography, createTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import { useAppDispatch } from "../../../../shared/hooks/sharedHooks";
import { enumToArray } from "../../../../shared/models/models";
import { CurrencyCode, ProductConditionCode, UrgencyCode } from "../../../../common/models/CommonModels";
import { ChangeRfq } from "../../models/models";
import { createRfq } from "../../customerMyRfqsSlice";
import ProductDetailsStep from "./ProductDetailsStep";
import RfqDetailsStep from "./RfqDetailsStep";
import PublishStep from "./PublishStep";

const steps = [
  {
    title: "Products details",
    component: <ProductDetailsStep />
  },
  {
    title: "RFQ details",
    component: <RfqDetailsStep />
  },
  {
    title: "Publish",
    component: <PublishStep />
  }
];

const CreateRfq = () => {
  const dispatch = useAppDispatch();
  const formMethods = useForm<ChangeRfq>();
  const navigate = useNavigate();
  const [activeStepIndex, setActiveStepIndex] = React.useState(0);

  const handleCreateRfq = async () => {
    const model = formMethods.getValues();
    model.productAlternativeNumber = model.productAlternativeNumber || undefined;
    model.maxUnitPrice = model.maxUnitPrice ? { id: CurrencyCode.eur, value: Number(model.maxUnitPrice) } : undefined;
    model.quantity = model.quantity ? Number(model.quantity) : undefined;
    model.productConditionCodes = model.productConditionCodes.map(x => Number(x));
    model.urgencyCode = model.urgencyCode ? Number(model.urgencyCode) : UrgencyCode.any;

    await dispatch(createRfq(model));
    navigate("/trading/my-rfqs");
  };

  const handleNext = async () => {
    await formMethods.trigger();

    if (!formMethods.formState.isValid) {
      return;
    }

    if (activeStepIndex === steps.length - 1) {
      handleCreateRfq();
      return;
    }

    setActiveStepIndex((prevActiveStepIndex) => prevActiveStepIndex + 1);
  };

  const handleBack = () => {
    if (activeStepIndex === 0) {
      navigate("/trading/my-rfqs");
      return;
    }

    setActiveStepIndex((prevActiveStepIndex) => prevActiveStepIndex - 1);
  };

  const init = async () => {
    const initialValues = {
      productConditionCodes: enumToArray<ProductConditionCode>(ProductConditionCode).map(code => `${code}` as any),
      urgencyCode: UrgencyCode.any,
      lastCall: moment().add(1, "w").toDate()
    };
    formMethods.reset(initialValues);
  };

  useEffect(() => {
    init();
  }, []);

  const theme = createTheme({
    components: {
      MuiStepper: {
        styleOverrides: {
          root: {
            ".MuiStepIcon-root.Mui-active": {
              color: "#333",
              fontWeight: 500
            },
            ".MuiStepLabel-label": {
              fontFamily: "'Work Sans', 'Open Sans', sans-serif"
            }
          }
        }
      }
    }
  });

  return (
    <ThemeProvider theme={theme}>
      <FormProvider {...formMethods}>
        <form id="createRfqForm">
          <Box className="grid grid-cols-4 gap-5 p-5">
            <Box className="col-span-2">
              <Stepper activeStep={activeStepIndex} className="mt-4">
                {steps.map((step, index) => (
                  <Step key={index}>
                    <StepLabel className="text-14-medium">
                      {step.title}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              <React.Fragment>{steps[activeStepIndex].component}</React.Fragment>
            </Box>

            <Box className="mt-16">
              <Button variant="contained" className="w-full" onClick={handleNext}>
                {activeStepIndex === steps.length - 1 ? "Publish RFQ" : "Next step"}
              </Button>

              <Box className="mt-3">
                <Button variant="outlined" className="w-full" onClick={handleBack}>
                  {activeStepIndex === 0 ? "Cancel" : "Back"}
                </Button>
              </Box>
            </Box>
          </Box>
        </form>
      </FormProvider>
    </ThemeProvider>
  );
};

export default CreateRfq;
